/**
 * Career Specialization Detail Page
 * Comprehensive career information for a specific specialization
 */

import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getSpecializationBySlug } from '../../../api/career-api';
import type { CareerSpecialization } from '../../../types';
import { Header } from '../../../../../shared/components/Header';
import { Footer } from '../../../../../shared/components/Footer';

const SpecializationDetailPage: React.FC = () => {
  const params = useParams();
  console.log('🎯🎯🎯 ALL params for spec page:', params);

  const domainSlug = params['domain-slug'];
  const specSlug = params['spec-slug'];
  console.log('🎯🎯🎯 Extracted - domainSlug:', domainSlug, 'specSlug:', specSlug);

  const [spec, setSpec] = useState<CareerSpecialization | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    console.log('🎯🎯🎯 useEffect - specSlug:', specSlug);
    if (specSlug) {
      console.log('🎯🎯🎯 Loading specialization...');
      loadSpecialization();
    } else {
      console.log('🎯🎯🎯 ❌ No specSlug found!');
    }
  }, [specSlug]);

  const loadSpecialization = async () => {
    console.log('🎯🎯🎯 loadSpecialization START - slug:', specSlug);
    try {
      setIsLoading(true);
      setError(null);
      console.log('🎯🎯🎯 Calling getSpecializationBySlug...');
      const data = await getSpecializationBySlug(specSlug!);
      console.log('🎯🎯🎯 Got specialization:', data?.name);
      setSpec(data);
    } catch (err: any) {
      console.error('🎯🎯🎯 ❌ ERROR:', err);
      setError(err.message || 'Failed to load specialization');
    } finally {
      console.log('🎯🎯🎯 Setting isLoading to false');
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading specialization...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error || !spec) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="text-6xl mb-4">😕</div>
            <h2 className="text-2xl font-bold mb-3">Specialization Not Found</h2>
            <p className="text-muted-foreground mb-6">
              {error || 'The specialization you are looking for does not exist.'}
            </p>
            <Link
              to="/explore/careers"
              className="inline-block bg-primary hover:bg-primary/90 text-white px-6 py-3 rounded-xl font-semibold transition-colors"
            >
              Back to Explore Careers
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-background">
      {/* Breadcrumb */}
      <div className="bg-muted/30 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/explore/careers" className="hover:text-primary transition-colors">
              Explore Careers
            </Link>
            <span>→</span>
            {spec.domain && (
              <>
                <Link
                  to={`/explore/careers/${spec.domain.slug}`}
                  className="hover:text-primary transition-colors"
                >
                  {spec.domain.name}
                </Link>
                <span>→</span>
              </>
            )}
            <span className="text-foreground font-medium">{spec.name}</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary via-primary to-emerald-700 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-6">
            <div className="text-7xl">{spec.icon_emoji || '💼'}</div>
            <div className="flex-1">
              <h1 className="text-4xl md:text-5xl font-extrabold mb-4">{spec.name}</h1>
              <p className="text-xl text-white/90 max-w-3xl mb-6">{spec.description}</p>

              {/* Badges */}
              <div className="flex flex-wrap gap-3">
                {spec.demand_level && (
                  <span className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg font-medium">
                    📈 {spec.demand_level} Demand
                  </span>
                )}
                {spec.job_outlook && (
                  <span className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg font-medium">
                    🎯 {spec.job_outlook} Outlook
                  </span>
                )}
                {spec.work_type && (
                  <span className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg font-medium">
                    🏢 {spec.work_type}
                  </span>
                )}
                {spec.automation_risk && (
                  <span className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg font-medium">
                    🤖 {spec.automation_risk} Automation Risk
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Salary Information */}
        {(spec.avg_salary_entry || spec.avg_salary_mid || spec.avg_salary_senior) && (
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-8 mb-12">
            <h2 className="text-2xl font-bold mb-6 text-green-900">💰 Salary Expectations</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {spec.avg_salary_entry && (
                <div className="bg-white rounded-xl p-6 text-center">
                  <div className="text-sm text-muted-foreground mb-2">Entry Level</div>
                  <div className="text-3xl font-extrabold text-primary">
                    ${(spec.avg_salary_entry / 1000).toFixed(0)}k
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">per year</div>
                </div>
              )}
              {spec.avg_salary_mid && (
                <div className="bg-white rounded-xl p-6 text-center">
                  <div className="text-sm text-muted-foreground mb-2">Mid-Level (3-5 years)</div>
                  <div className="text-3xl font-extrabold text-primary">
                    ${(spec.avg_salary_mid / 1000).toFixed(0)}k
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">per year</div>
                </div>
              )}
              {spec.avg_salary_senior && (
                <div className="bg-white rounded-xl p-6 text-center">
                  <div className="text-sm text-muted-foreground mb-2">Senior Level (5+ years)</div>
                  <div className="text-3xl font-extrabold text-primary">
                    ${(spec.avg_salary_senior / 1000).toFixed(0)}k
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">per year</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* What You'll Learn - Narrative Style */}
        {spec.what_you_learn && (
          <div className="mb-16">
            <h2 className="text-3xl font-bold mb-6 text-foreground">📚 What You'll Learn</h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-xl text-foreground leading-relaxed mb-6 whitespace-pre-line">
                {spec.what_you_learn}
              </p>
            </div>
          </div>
        )}

        {/* Day in the Life - Story Format */}
        {spec.day_in_life && (
          <div className="mb-16">
            <h2 className="text-3xl font-bold mb-6 text-foreground">🌅 A Day in the Life</h2>
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl p-8 md:p-10">
              <p className="text-xl text-gray-800 leading-relaxed whitespace-pre-line">
                {spec.day_in_life}
              </p>
            </div>
          </div>
        )}

        {/* Skills Section - Database Driven */}
        {((spec.required_skills && Array.isArray(spec.required_skills) && spec.required_skills.length > 0) ||
          (spec.soft_skills && Array.isArray(spec.soft_skills) && spec.soft_skills.length > 0)) && (
          <div className="mb-16">
            <h2 className="text-3xl font-bold mb-6 text-foreground">🎯 Skills You'll Need</h2>

            {spec.skills_intro && (
              <p className="text-lg text-foreground leading-relaxed mb-6">
                {spec.skills_intro}
              </p>
            )}

            {spec.required_skills && Array.isArray(spec.required_skills) && spec.required_skills.length > 0 && (
              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-4 text-primary">🔧 Technical Skills</h3>
                <p className="text-lg text-foreground leading-relaxed mb-3">
                  {spec.skills_intro ? (
                    <>
                      <span className="font-semibold">{spec.required_skills.join(', ')}</span>
                    </>
                  ) : (
                    <>
                      To excel in this field, you'll need to master: <span className="font-semibold">{spec.required_skills.join(', ')}</span>.
                      Don't worry if these sound intimidating—every expert started as a beginner! Most of these skills can be learned through online courses, bootcamps, or university programs.
                    </>
                  )}
                </p>
              </div>
            )}

            {spec.soft_skills && Array.isArray(spec.soft_skills) && spec.soft_skills.length > 0 && (
              <div>
                <h3 className="text-xl font-semibold mb-4 text-emerald-600">💡 Soft Skills</h3>
                <p className="text-lg text-foreground leading-relaxed">
                  {!spec.skills_intro && (
                    <>
                      Beyond technical know-how, successful professionals in this field typically have strong{' '}
                    </>
                  )}
                  <span className="font-semibold">{spec.soft_skills.join(', ')}</span>
                  {!spec.skills_intro && (
                    <> skills. These "people skills" are just as important as technical abilities and will help you collaborate effectively, solve complex problems, and advance in your career.</>
                  )}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Career Paths - Database Driven */}
        {spec.career_paths && Array.isArray(spec.career_paths) && spec.career_paths.length > 0 && (
          <div className="mb-16">
            <h2 className="text-3xl font-bold mb-6 text-foreground">🚀 Where Can This Take You?</h2>
            <p className="text-lg text-foreground leading-relaxed mb-6">
              {spec.career_paths_intro || `${spec.name} opens doors to exciting career opportunities. As you grow your skills and experience, you could pursue roles such as:`}
            </p>
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-8">
              <div className="space-y-4">
                {spec.career_paths.map((path, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <span className="text-2xl">💼</span>
                    <div>
                      <h4 className="font-bold text-lg text-gray-900">{path}</h4>
                      {index === 0 && <p className="text-gray-600 mt-1">Great entry point for beginners</p>}
                      {spec.career_paths && index === spec.career_paths.length - 1 && <p className="text-gray-600 mt-1">Leadership role with years of experience</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Education - Database Driven */}
        {spec.education_level && (
          <div className="mb-16">
            <h2 className="text-3xl font-bold mb-6 text-foreground">🎓 How to Get Started</h2>
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl p-8 md:p-10">
              <p className="text-xl text-gray-800 leading-relaxed mb-4">
                <span className="font-bold">Education Requirements:</span> {spec.education_level}
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                {spec.education_intro || `Remember, your educational path doesn't have to be traditional! Many successful professionals in this field come from diverse backgrounds—some have formal degrees, others are self-taught, and many combine different learning methods. What matters most is your passion, dedication, and willingness to keep learning.`}
              </p>
            </div>
          </div>
        )}

        {/* Top Companies - Database Driven */}
        {spec.top_companies && Array.isArray(spec.top_companies) && spec.top_companies.length > 0 && (
          <div className="mb-16">
            <h2 className="text-3xl font-bold mb-6 text-foreground">🏢 Dream Companies Are Hiring</h2>
            <p className="text-lg text-foreground leading-relaxed mb-6">
              {spec.companies_intro || `Some of the world's most innovative and exciting companies are actively looking for talented professionals in ${spec.name}. Imagine working at companies like:`}
            </p>
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-3xl p-8">
              <div className="flex flex-wrap gap-4 justify-center">
                {spec.top_companies.map((company, index) => (
                  <span
                    key={index}
                    className="text-2xl font-bold text-gray-800 px-6 py-3 bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm hover:shadow-md transition-shadow"
                  >
                    {company}
                  </span>
                ))}
              </div>
              <p className="text-center text-gray-600 mt-6 text-lg">
                ...and thousands more companies worldwide!
              </p>
            </div>
          </div>
        )}

        {/* Industries - Database Driven */}
        {spec.industries && Array.isArray(spec.industries) && spec.industries.length > 0 && (
          <div className="mb-16">
            <h2 className="text-3xl font-bold mb-6 text-foreground">🌐 Work Across Different Industries</h2>
            <p className="text-lg text-foreground leading-relaxed mb-6">
              {spec.industries_intro || `The beauty of ${spec.name} is its versatility! Your skills will be valuable across many industries. Whether you're passionate about healthcare, entertainment, finance, or education—there's a place for you. You could find yourself working in:`}
            </p>
            <div className="prose prose-lg max-w-none">
              <p className="text-xl text-foreground">
                <span className="font-semibold text-primary">{spec.industries.join(' • ')}</span>
              </p>
            </div>
            {!spec.industries_intro && (
              <p className="text-lg text-gray-600 mt-6 leading-relaxed">
                This flexibility means you can align your career with your personal interests and values, making work more meaningful and enjoyable.
              </p>
            )}
          </div>
        )}

        {/* Research Areas - Database Driven */}
        {spec.research_areas && Array.isArray(spec.research_areas) && spec.research_areas.length > 0 && (
          <div className="mb-16">
            <h2 className="text-3xl font-bold mb-6 text-foreground">🚀 The Future is Being Built Now</h2>
            <p className="text-lg text-foreground leading-relaxed mb-6">
              {spec.research_intro || `The field is constantly evolving! Here are some of the most exciting cutting-edge areas where innovation is happening right now:`}
            </p>
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-3xl p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {spec.research_areas.map((area, index) => (
                  <div key={index} className="flex items-start gap-4 bg-white/60 backdrop-blur-sm rounded-2xl p-4">
                    <span className="text-3xl">🔬</span>
                    <div>
                      <h4 className="font-bold text-lg text-gray-900">{area}</h4>
                      <p className="text-sm text-gray-600 mt-1">Active research & development</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {!spec.research_intro && (
              <p className="text-lg text-gray-600 mt-6 leading-relaxed">
                By entering this field now, you'll be at the forefront of these innovations, potentially contributing to breakthroughs that change the world!
              </p>
            )}
          </div>
        )}

        {/* Certifications - Database Driven */}
        {spec.certifications && Array.isArray(spec.certifications) && spec.certifications.length > 0 && (
          <div className="mb-16">
            <h2 className="text-3xl font-bold mb-6 text-foreground">📜 Boost Your Career with Certifications</h2>
            <p className="text-lg text-foreground leading-relaxed mb-6">
              {spec.certifications_intro || `While not always required, professional certifications can significantly boost your resume, prove your expertise, and sometimes even increase your salary! Here are industry-recognized certifications worth considering:`}
            </p>
            <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-3xl p-8">
              <div className="space-y-3">
                {spec.certifications.map((cert, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-2xl p-5 flex items-center gap-4 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="text-3xl">🏆</div>
                    <div className="flex-1">
                      <h4 className="font-bold text-lg text-gray-900">{cert}</h4>
                      <p className="text-sm text-gray-600">Industry-recognized credential</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {!spec.certifications_intro && (
              <p className="text-lg text-gray-600 mt-6 leading-relaxed">
                Many of these certifications can be earned online at your own pace, making them accessible even while you're studying or working!
              </p>
            )}
          </div>
        )}

        {/* CTA Section */}
        <div className="bg-gradient-to-br from-primary via-primary to-emerald-700 text-white rounded-2xl p-10 text-center">
          <h3 className="text-3xl font-bold mb-4">Ready to Pursue This Career?</h3>
          <p className="text-xl text-white/90 mb-6 max-w-2xl mx-auto">
            Find universities and programs that offer courses in {spec.name}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/university"
              className="inline-block bg-white text-primary hover:bg-white/90 px-8 py-3 rounded-xl font-semibold transition-colors"
            >
              Browse Universities
            </Link>
            <Link
              to="/quiz"
              className="inline-block bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white border-2 border-white px-8 py-3 rounded-xl font-semibold transition-colors"
            >
              Take Career Quiz
            </Link>
          </div>
        </div>
      </div>
      </div>
      <Footer />
    </>
  );
};

export default SpecializationDetailPage;
